import { CaretDownIcon } from "@phosphor-icons/react";
import clsx from "clsx";
import { useState } from "react";
import { useIntl } from "react-intl";

import { ButtonBase } from "@/components/ButtonBase/ButtonBase";
import { Chip } from "@/components/Chip/Chip";
import { Collapse } from "@/components/Collapse/Collapse";
import { Divider } from "@/components/Divider/Divider";
import {
  DrawerContent,
  DrawerTopBar,
  SwipeableDrawer,
} from "@/components/Drawer";
import { IconTraveller } from "@/components/Icons";
import { TravellerDetails } from "@/features/travellers/components/TravellerDetails";
import type { Traveller } from "@/features/travellers/types/types";
import { getAgeFromDateOfBirth } from "@/utils/getAgefromDateOfBirth";

type Props = {
  open: boolean;
  travellers: Traveller[];
  leadTravellerId: string | null;
  onClose: () => void;
};

export const BookedHotelTravellersDrawer = ({
  open,
  travellers,
  leadTravellerId,
  onClose,
}: Props) => {
  const intl = useIntl();

  const [selectedTraveller, setSelectedTraveller] = useState<Traveller | null>(
    null,
  );

  const handleTravellerClick = (traveller: Traveller) => {
    setSelectedTraveller((prev) =>
      prev?.id === traveller.id ? null : traveller,
    );
  };

  return (
    <SwipeableDrawer
      onClose={onClose}
      open={open}
      backgroundColor="background"
      hasFullHeight
    >
      <DrawerTopBar
        onCloseClick={onClose}
        title={intl.formatMessage({
          defaultMessage: "Traveller details",
          description: "Title for the traveller details drawer",
        })}
      />
      <DrawerContent>
        <ul className="flex flex-col gap-4">
          {travellers.map((traveller) => (
            <li className="section-card" key={traveller.id}>
              <ButtonBase
                onClick={() => handleTravellerClick(traveller)}
                className="flex w-full items-center justify-between gap-1"
              >
                <IconTraveller className="size-6 shrink-0" />
                <div className="me-auto text-start">
                  <div className="text-text-primary">
                    {traveller.firstName} {traveller.lastName}
                  </div>
                  <div className="space-x-2">
                    <Chip
                      color="neutral-surface"
                      variant="outlined"
                      shape="rounded"
                      label={intl.formatMessage(
                        {
                          defaultMessage: "Age: {age}",
                          description:
                            "Label for traveller's age at hotel booking summary page",
                        },
                        { age: getAgeFromDateOfBirth(traveller.birthDate) },
                      )}
                    />
                    {traveller.id === leadTravellerId ? (
                      <Chip
                        color="accent-soft"
                        shape="rounded"
                        label={intl.formatMessage({
                          defaultMessage: "Main traveller",
                          description:
                            "Badge or label identifying the primary / main traveller in a booking",
                        })}
                      />
                    ) : null}
                  </div>
                </div>
                <CaretDownIcon
                  color="inherit"
                  size={20}
                  weight="bold"
                  className={clsx("transition-transform duration-200", {
                    "rotate-180": selectedTraveller?.id === traveller.id,
                  })}
                />
              </ButtonBase>
              <Collapse in={selectedTraveller?.id === traveller.id}>
                <div className="mt-2 flex flex-col gap-2">
                  <Divider />
                  <div className="p-2">
                    <TravellerDetails
                      hidePassportDetails
                      traveller={traveller}
                    />
                  </div>
                </div>
              </Collapse>
            </li>
          ))}
        </ul>
      </DrawerContent>
    </SwipeableDrawer>
  );
};
